import Link from 'client/components/Link';

export default function PrivacyContent() {
  return (
    <div className="Legal-content">
      <h1>Eth.id Privacy Policy</h1>
      <p>
        <b>Last updated: June 25, 2022</b>
      </p>
      <p>
        This Privacy Policy explains how Eth Id, Inc. d/b/a Eth.id and its
        subsidiaries and affiliated companies (“<b>Company</b>”, “<b>we</b>,” “
        <b>us</b>”, or “<b>our</b>”) collects, uses, and discloses information
        about you. This Privacy Policy applies when you use our website and
        other online products and services that link to this Privacy Policy
        (collectively, our “ Services”), contact our customer service team,
        engage with us on social media, or otherwise interact with us.
      </p>

      <p>
        We may change this Privacy Policy from time to time. If we make changes,
        we will notify you by revising the date at the top of this policy and,
        in some cases, we may provide you with additional notice (such as adding
        a statement to our website or sending you a notification). We encourage
        you to review this Privacy Policy regularly to stay informed about our
        information practices and the choices available to you.
      </p>

      <h2>ETH.ID DOMAIN ASSOCIATIONS</h2>
      <p>
        The Services allow you to associate information with a unique name (each
        a “<b>Eth.id domain</b>”). Your Eth.id domain can be associated with a
        third-party website URL, social media handle, cryptocurrency payment
        address, email address, location data, text data, or other online data
        that you choose to associate with your Eth.id domain (“
        <b>Associated Data</b>”) in accordance with the{' '}
        <Link to="/terms">Eth.id Terms of Service</Link>.
      </p>

      <p>
        Due to the inherent nature of the Services, any Associated Data will be
        public and may not be able to be removed or deleted. Your Associated
        Data may reveal information about you and those with whom you are
        transacting. Such information can potentially be correlated with you and
        others now or in the future by any party who chooses to do so, including
        law enforcement.
      </p>

      <h2>COLLECTION OF INFORMATION</h2>
      <p>
        <b>Information You Provide to Us</b>
      </p>

      <p>
        We collect information you provide directly to us. For example, you
        share information directly with us when you create an account, fill out
        a form, submit or post Associated Data through our Services, make a
        purchase, communicate with us via third-party platforms, participate in
        a contest or promotion, request customer support, or otherwise
        communicate with us. The types of personal information we may collect
        include your name, email address, phone number, passwords, referral
        codes, blockchain addresses, social media handles, and any other
        information you choose to provide.
      </p>

      <p>
        <b>Information We Collect Automatically When You Interact with Us</b>
      </p>

      <p>
        When you access or use our Services or otherwise transact business with
        us, we automatically collect certain information, including:
      </p>

      <ol>
        <li>
          <b>Transactional Information:</b> When you make a purchase, we collect
          information about the transaction, such as the purchase price, the
          date of the transaction, and the Eth.id domain you are licensing from
          us.
        </li>
        <li>
          <b>Usage Information:</b> We collect information about your use of our
          Services, including data about third-party queries via the Company’s
          API.
        </li>
      </ol>

      <p>
        <b>Information We Collect from Other Sources</b>
      </p>

      <p>
        We obtain information from third-party sources. For example, when you
        connect your cryptocurrency wallet to our Services, we collect
        information about the various tokens you hold and your wallet’s
        transaction history and associate such information with your Eth.id
        domain. We may also collect information about you from social media
        platforms and payment processors, including credit card and other
        personal information you provide to our payment processors when making a
        purchase.
      </p>

      <p>
        <b>Information We Derive</b>
      </p>

      <p>
        We may derive information or draw inferences about you based on the
        information we collect. For example, we may make inferences about you
        and your account based on the tokens you hold and/or your wallet’s
        transaction history.
      </p>

      <h2>USE OF INFORMATION</h2>
      <p>
        We use the information we collect to administer your account. We also
        use the information we collect to:
      </p>

      <ol>
        <li>
          Enable third parties to access Associated Data via Company’s API;
        </li>
        <li>Provide, maintain, and improve our products and services;</li>
        <li>
          Process transactions and send you related information, including
          confirmations, receipts, invoices, and customer experience surveys;
        </li>
        <li>
          Send you technical notices, security alerts, and support and
          administrative messages;
        </li>
        <li>
          Respond to your comments and questions and provide customer service;
        </li>
        <li>
          Monitor and analyze trends, usage, and activities in connection with
          our Services;
        </li>
        <li>
          Facilitate contests, sweepstakes, and promotions and process and
          deliver entries and rewards;
        </li>
        <li>
          Detect, investigate, and prevent security incidents and other
          malicious, deceptive, fraudulent, or illegal activity and protect the
          rights and property of Company and others;
        </li>
        <li>Debug to identify and repair errors in our Services;</li>
        <li>Comply with our legal and financial obligations; and</li>
        <li>
          Carry out any other purpose described to you at the time the
          information was collected.
        </li>
      </ol>
      <h2>SHARING OF INFORMATION</h2>
      <p>
        As noted in Eth.id Domain Associations, any Associated Data is made
        publicly viewable, may be shared with any third party that calls it via
        Company’s API, and may become permanently public. Other than Associated
        Data, we share personal information in the following circumstances or as
        otherwise described in this policy:
      </p>

      <ol>
        <li>
          We share personal information with vendors, service providers, and
          consultants that need access to personal information in order to
          perform services for us, such as companies that assist us with web
          hosting, payment processing, fraud prevention, customer service, and
          marketing and advertising.
        </li>
        <li>
          We may disclose personal information if we believe that disclosure is
          in accordance with, or required by, any applicable law or legal
          process, including lawful requests by public authorities to meet
          national security or law enforcement requirements.
        </li>
        <li>
          We may share personal information if we believe that your actions are
          inconsistent with our user agreements or policies, if we believe that
          you have violated the law, or if we believe it is necessary to protect
          the rights, property, and safety of Company, our users, the public, or
          others.
        </li>
        <li>
          We share personal information with our lawyers and other professional
          advisors where necessary to obtain advice or otherwise protect and
          manage our business interests.
        </li>
        <li>
          We may share personal information in connection with, or during
          negotiations concerning, any merger, sale of company assets,
          financing, or acquisition of all or a portion of our business by
          another company.
        </li>
        <li>
          Personal information is shared between and among Company and our
          current and future parents, affiliates, and subsidiaries and other
          companies under common control and ownership.
        </li>
        <li>
          We share personal information with your consent or at your direction.
        </li>
      </ol>
      <p>
        We also share aggregated or de-identified information that cannot
        reasonably be used to identify you.
      </p>

      <h2>TRANSFER OF INFORMATION TO THE UNITED STATES AND OTHER COUNTRIES</h2>
      <p>
        Company is headquartered in the United States, and we have operations
        and service providers in the United States. Therefore, we and our
        service providers may transfer your personal information to, or store or
        access it in, jurisdictions that may not provide levels of data
        protection that are equivalent to those of your home jurisdiction. We
        will take steps to ensure that your personal information receives an
        adequate level of protection in the jurisdictions in which we process
        it.
      </p>

      <h2>YOUR CHOICES</h2>
      <p>
        <b>Account Information</b>
      </p>

      <p>
        You may update and correct certain account information at any time by
        logging into your account or email us at support@eth.id. If you wish to
        delete your account, please email us at support@eth.id, but note
        Associated Data may not be able to be deleted and we may retain certain
        information as required by law or for our legitimate business purposes.
      </p>

      <p>
        <b>Communications Preferences</b>
      </p>

      <p>
        You may opt out of receiving promotional emails from Company by
        following the instructions in those communications or by emailing us at
        support@eth.id. If you opt out, we may still send you non-promotional
        emails, such as those about your account or our ongoing business
        relations.
      </p>

      <h2>YOUR CALIFORNIA PRIVACY RIGHTS</h2>
      <p>
        The California Consumer Privacy Act or “CCPA” (Cal. Civ. Code § 1798.100
        et seq.) affords consumers residing in California certain rights with
        respect to their personal information. If you are a California resident,
        this section applies to you.
      </p>

      <p>
        <b>
          <i>California Consumer Privacy Act</i>
        </b>
      </p>

      <p>
        In the preceding 12 months, we have collected the following categories
        of personal information: identifiers, commercial information, internet
        or other electronic network activity, and inference data.
      </p>

      <p>
        For details about the precise data points we collect and the categories
        of sources of such collection, please see the Collection of Information
        section above. We collect personal information for the business and
        commercial purposes described in the Use of Information section above.
        We launched the service in June 2022, since then, we have disclosed the
        following categories of personal information for business purposes to
        the following categories of recipients:
      </p>

      <p>
        <b>
          <i>Personal Information: Identifiers</i>
        </b>
      </p>

      <ul>
        <li>Recipients:</li>
        <li>Service providers; and</li>
        <li>people who view your Associated Data.</li>
      </ul>
      <p>
        <b>
          <i>Personal Information: Commercial Information</i>
        </b>
      </p>
      <ul>
        <li>Recipients: Service providers</li>
      </ul>
      <p>Company does not sell your personal information.</p>

      <h2>ADDITIONAL DISCLOSURES FOR INDIVIDUALS IN EUROPE</h2>
      <p>
        If you are located in the European Economic Area (EEA), the United
        Kingdom, or Switzerland, you have certain rights and protections under
        the law regarding the processing of your personal data, and this section
        applies to you.
      </p>

      <p>
        <b>‍Legal Basis for Processing</b>
      </p>

      <p>
        When we process your personal data, we will do so in reliance on the
        following lawful bases:
      </p>

      <ul>
        <li>
          To perform our responsibilities under our contract with you (e.g.,
          processing payments for and providing the products and services you
          requested).
        </li>
        <li>
          When we have a legitimate interest in processing your personal data to
          operate our business or protect our interests (e.g., to provide,
          maintain, and improve our products and services and communicate with
          you).
        </li>
        <li>
          To comply with our legal obligations (e.g., to maintain a record of
          your consents and track those who have opted out of marketing
          communications).
        </li>
        <li>
          When we have your consent to do so (e.g., when you opt in to receive
          marketing communications from us). When consent is the legal basis for
          our processing your personal data, you may withdraw such consent at
          any time.
        </li>
      </ul>
      <p>
        <b>Data Retention</b>
      </p>

      <p>
        We store personal data associated with your account as long as necessary
        to carry out the purposes for which we originally collected it and for
        other legitimate business purposes, including to meet our legal,
        regulatory, or other compliance obligations.
      </p>
      <p>
        <b>Data Subject Requests</b>
      </p>
      <p>
        Subject to certain limitations, you have the right to request access to
        the personal data we hold about you and to receive your data in a
        portable format, the right to ask that your personal data be corrected
        or erased, and the right to object to, or request that we restrict,
        certain processing. If you would like to exercise any of these rights,
        please email us at support@eth.id.
      </p>
      <p>
        <b>Service Migration</b>
      </p>
      <p>
        We reserve the right to migrate all or any part of the Services from a
        centralized directory maintained by Company or an affiliate to a
        decentralized directory maintained on a distributed ledger of which
        Company has no ownership or control (“<b>Distributed Ledger</b>”).You
        acknowledge and agree that, notwithstanding anything in this Privacy
        Policy to the contrary, in the event we do migrate all or any part of
        the Services to a Distributed Ledger, (i) Company will have no further
        obligations to you under this Privacy Policy whatsoever and (ii) you
        shall look solely to the Distributed Ledger (and, in any event, not
        Company) for the receipt and enjoyment of all of your rights under this
        Privacy Policy.
      </p>
      <p>
        <b>Questions or Complaints</b>
      </p>
      <p>
        If you have a concern about our processing of personal data that we are
        not able to resolve, you have the right to lodge a complaint with the
        Data Protection Authority where you reside. Contact details for your
        Data Protection Authority can be found using the links below:
      </p>
      <p>
        For individuals in the EEA:{' '}
        <a href="https://edpb.europa.eu/about-edpb/board/members_en">
          https://edpb.europa.eu/about-edpb/board/members_en
        </a>
      </p>
      <p>
        For individuals in the UK:{' '}
        <a href="https://ico.org.uk/global/contact-us/">
          https://ico.org.uk/global/contact-us/
        </a>
      </p>
      <p>
        For individuals in Switzerland:{' '}
        <a href="https://www.edoeb.admin.ch/edoeb/en/home/the-fdpic/contact.html">
          https://www.edoeb.admin.ch/edoeb/en/home/the-fdpic/contact.html
        </a>
      </p>
      <h2>CONTACT US</h2>
      <p>
        If you have any questions about this Privacy Policy, please contact us
        at support@eth.id.
      </p>
    </div>
  );
}
